import type { SearchableSelectOptions } from '@cx/ui/SearchableSelect';
import type { ComboBoxOption } from '@interstate/components/ComboBox';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputEventValue } from '@interstate/components/TextInput';
import { dateUtils, formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import type { UpdateFlexBuyPaymentGridDataPayload } from '@makemydeal/dr-offer-redux';
import { utils as offerReduxUtils } from '@makemydeal/dr-offer-redux';
import type { OfferType, PaymentGridRow } from '@makemydeal/dr-platform-types';
import { CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import numeral from 'numeral';
import {
    AMOUNT_PAID_BY_CUSTOMER,
    CASH_AMOUNT_PAID,
    CASH_DOWN,
    DUE_AT_SIGNING,
    OUT_OF_POCKET,
    CASH_RECEIVED
} from '../../constants';

export type ComboBoxPairs = {
    id: number;
    name: string;
};

export const termIdToOptionMapper = ({ id, months }: any): SearchableSelectOptions => ({
    value: id,
    label: `${months} Months`
});

export const ENTER_KEY_CODE = 'Enter';

export const formatAnnualMiles = (miles: number): string => `${numeral(miles).format('0,0')} Miles`;

export const addValueSuffix = (value: number | undefined): string => {
    const suffix = 'Month';

    if (value == null) {
        return '';
    }

    const label = [value, suffix].join(' ');
    return value > 1 ? `${label}s` : label;
};

export const removeValueSuffix = (value: string): string => {
    return value.replace(' Months', '').replace(' Month', '');
};

export const formatDate = (isoDate: string): string => {
    const date = new Date(isoDate);
    return isoDate && dateUtils.isValidDate(date) ? dateUtils.formatUtcDateTimeToDateOnly(isoDate, 'mmddyyyy') : '';
};

export const formatSingleCardViewPaymentDetails = (currentOfferType: OfferType, monthlyPayment?: number) => {
    return currentOfferType !== CASH ? `${formatDollarsAndCents(monthlyPayment)}/mo` : '';
};
export const formatPaymentDetailsCashDown = (currentOfferType: OfferType, amountPaidByCustomerLease: number) => {
    const label = currentOfferType === LEASE ? AMOUNT_PAID_BY_CUSTOMER : CASH_DOWN;
    return `${label} - ${formatDollarsAndCents(amountPaidByCustomerLease)}`;
};
export const formatMultiCardViewPaymentDetails = (
    currentOfferType: OfferType,
    monthlyPayment?: number,
    cashAmountDue?: number,
    selectedTermId?: string
) => {
    // add indicator L C F for all offer types
    const offerTypeIndicator = currentOfferType[0].toUpperCase();
    let paymentValue = offerTypeIndicator;

    // if offer type is not cash, add term value
    if (currentOfferType !== CASH) {
        const months = parseSelectedTermMonths(selectedTermId || '');
        paymentValue += months || '';
    }

    // add payment value. for cash it is total, for finance/lease it is monthly payment
    // no need to check for cashAmountDue or monthlyPayment, formatDollarsAndCents will handle it
    if (currentOfferType === CASH) {
        paymentValue += ` - ${formatDollarsAndCents(cashAmountDue)}`;
    } else {
        paymentValue += ` - ${formatDollarsAndCents(monthlyPayment)}/mo`;
    }

    return paymentValue;
};

export const getNewTarget = (event: any) => {
    const inputValue = (event.target as HTMLInputElement).value;
    return {
        target: {
            value: inputValue.replace(/,/g, '')
        }
    } as InterstateOnChangeEvent<TextInputEventValue>;
};

/**
 * Determine which label to use for "Cash Down" in the UI
 */
export const generateCashDownLabel = (
    offerType: OfferType,
    deductDueAtSigning: boolean,
    isTargetOutOfPocket: boolean,
    isEnhancedDealDetailsEnabled: boolean,
    isTargetOutOfPocketLeaseEnabled: boolean,
    isDueAtSigningEnhancementsEnabled: boolean,
    isEquityAllocationOrderEnabled = false
): string => {
    switch (offerType) {
        case LEASE:
            if (isEquityAllocationOrderEnabled) {
                return CASH_RECEIVED;
            }
            if (isDueAtSigningEnhancementsEnabled) {
                if (deductDueAtSigning) {
                    return CASH_AMOUNT_PAID;
                }
                return isTargetOutOfPocket ? CASH_AMOUNT_PAID : CASH_DOWN;
            }

            if (!isTargetOutOfPocketLeaseEnabled) {
                if (deductDueAtSigning) {
                    return isEnhancedDealDetailsEnabled ? OUT_OF_POCKET : DUE_AT_SIGNING;
                }
            } else {
                // isTargetOutOfPocketLeaseEnabled is true
                if (deductDueAtSigning || isTargetOutOfPocket) {
                    return OUT_OF_POCKET;
                }
            }
            // This is the default case for LEASE when no other conditions are met.
            return CASH_DOWN;

        case FINANCE:
        case CASH:
        default:
            return CASH_DOWN;
    }
};

export const buildComboOption = (option: ComboBoxPairs): ComboBoxOption => {
    const { id, name } = option;
    return {
        id: id.toString(),
        value: id.toString(),
        label: name.toString()
    };
};

export const areStringsEqual = (input: string | undefined, key: string) => {
    if (!input) return false;
    return input === key;
};

export const getKeyWithLoadingItem = (data: PaymentGridRow[] | UpdateFlexBuyPaymentGridDataPayload) =>
    data.some((group) => group.items.some((item) => item?.isLoading));

export const parseSelectedTermMonths = offerReduxUtils.parseSelectedTermMonths;

/**
 * Tooltip copy for the Term field's override indicator when no lender program covers the requested term.
 * Returns '' when `months` is unknown; callers treat '' as "render no indicator".
 */
export const getTermNotSupportedMessage = (months?: number, previousMonths?: number): string => {
    if (!months) {
        return '';
    }
    return previousMonths
        ? `No lender program supports ${months} months (previous value was ${previousMonths} months). Payment shown is estimated, not lender-backed.`
        : `No lender program supports ${months} months. Payment shown is estimated, not lender-backed.`;
};
